<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import DocumentSigningSettings from './components/DocumentSigningSettings.vue';
import AccessLockSettings from './components/AccessLockSettings.vue';
import SectionLayout from './components/SectionLayout.vue';

import AccessToken from '../profile/AccessToken.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useI18n } from 'vue-i18n';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import countries from 'shared/constants/countries.js';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const DEFAULT_ACCENT_COLOR = '#7045AE';

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    TextArea,
    AccountId,
    BuildInfo,
    AccountDelete,
    AudioTranscription,
    DocumentSigningSettings,
    AccessLockSettings,
    SectionLayout,
    WithLabel,
    NextInput,
    AccessToken,
    ComboBox,
    ColorInput,
  },
  setup() {
    const { t } = useI18n();
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId, t };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      subjectLineTemplate: '',
      labelPrefix: '',
      features: {},
      autoResolveDuration: null,
      latestChatwootVersion: null,
      webhookSignatureSecret: '',
      showDeletePopup: false,
      contentContext: {
        business_name: '',
        business_type: 'venue',
        location: '',
        target_audience: '',
        offerings: '',
      },
      defaultCurrency: 'EUR',
      defaultTaxRate: '',
      timezone: 'Europe/Brussels',
      invoiceProfile: {
        legal_name: '',
        vat_id: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postal_code: '',
        region: '',
        country: '',
        invoice_email: '',
        invoice_phone: '',
        accent_color: DEFAULT_ACCENT_COLOR,
      },
      logoFile: null,
      logoUrl: '',
      removeLogo: false,
      countryOptions: countries.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAudioTranscriptionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
    },
    showDocumentSigningSettings() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.PLANNING_EVENT_DOCUMENTS
      );
    },
    showAccessLockSettings() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.IP_ACCESS_LOCK
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    timezoneOptions() {
      const options = timeZoneOptions();
      if (options.some(option => option.value === 'UTC')) return options;
      return [{ label: 'UTC (UTC+00:00)', value: 'UTC' }, ...options];
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },

    getWebhookSignatureSecret() {
      return this.webhookSignatureSecret.toString();
    },

    confirmPlaceHolderText() {
      return `${this.$t(
        'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.PLACE_HOLDER',
        {
          accountName: this.name,
        }
      )}`;
    },
    isMarkedForDeletion() {
      const { custom_attributes = {} } = this.currentAccount;
      return !!custom_attributes.marked_for_deletion_at;
    },
    markedForDeletionDate() {
      const { custom_attributes = {} } = this.currentAccount;
      if (!custom_attributes.marked_for_deletion_at) return null;
      return new Date(custom_attributes.marked_for_deletion_at);
    },
    markedForDeletionReason() {
      const { custom_attributes = {} } = this.currentAccount;
      return custom_attributes.marked_for_deletion_reason || 'manual_deletion';
    },
    formattedDeletionDate() {
      if (!this.markedForDeletionDate) return '';
      return this.markedForDeletionDate.toLocaleString();
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          support_email,
          subject_line_template,
          features,
          webhook_signature_secret,
          latest_chatwoot_version: latestChatwootVersion,
          auto_resolve_duration,
          label_prefix,
        } = this.getAccount(this.accountId);

        this.$root.$i18n.locale = this.uiSettings?.locale || locale;
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.subjectLineTemplate = subject_line_template || '';
        this.labelPrefix = label_prefix || '';
        this.features = features;
        this.autoResolveDuration = auto_resolve_duration;
        this.latestChatwootVersion = latestChatwootVersion;
        this.webhookSignatureSecret = webhook_signature_secret;

        const contentCtx =
          this.currentAccount?.custom_attributes?.content_context || {};
        this.contentContext = {
          business_name: contentCtx.business_name || '',
          business_type: contentCtx.business_type || 'venue',
          location: contentCtx.location || '',
          target_audience: contentCtx.target_audience || '',
          offerings: contentCtx.offerings || '',
        };

        const settings = this.currentAccount?.settings || {};
        this.defaultCurrency = settings.default_currency || 'EUR';
        this.defaultTaxRate = settings.default_tax_rate ?? '';
        this.timezone = settings.timezone || 'Europe/Brussels';
        this.invoiceProfile = {
          legal_name: settings.legal_name || '',
          vat_id: settings.vat_id || '',
          address_line1: settings.address_line1 || '',
          address_line2: settings.address_line2 || '',
          city: settings.city || '',
          postal_code: settings.postal_code || '',
          region: settings.region || '',
          country: settings.country || '',
          invoice_email: settings.invoice_email || '',
          invoice_phone: settings.invoice_phone || '',
          accent_color: settings.accent_color || DEFAULT_ACCENT_COLOR,
        };
        this.logoUrl = this.currentAccount?.logo_url || '';
        this.logoFile = null;
        this.removeLogo = false;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          subject_line_template: this.subjectLineTemplate,
          label_prefix: this.labelPrefix,
        });
        // If user locale is set, update the locale with user locale
        if (this.uiSettings?.locale) {
          this.$root.$i18n.locale = this.uiSettings?.locale;
        } else {
          // If user locale is not set, update the locale with account locale
          this.$root.$i18n.locale = this.locale;
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
    async onCopyToken(value) {
      await copyTextToClipboard(value);
      useAlert(this.t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
    },

    async onResetWebhookSecret() {
      const response = await this.$store.dispatch(
        'accounts/resetWebhookSignatureSecret'
      );
      this.webhookSignatureSecret = response.webhook_signature_secret;
      if (response) {
        useAlert(this.t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
      }
    },

    async updateContentContext() {
      try {
        await this.$store.dispatch('accounts/update', {
          content_context: this.contentContext,
        });
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    async updateTimezone() {
      try {
        await this.$store.dispatch('accounts/update', {
          timezone: this.timezone || 'Europe/Brussels',
        });
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    async updatePricingDefaults() {
      try {
        await this.$store.dispatch('accounts/update', {
          default_currency: this.defaultCurrency,
          default_tax_rate:
            this.defaultTaxRate !== '' ? Number(this.defaultTaxRate) : null,
        });
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    async updateInvoiceProfile() {
      try {
        const country = (this.invoiceProfile.country || '')
          .trim()
          .toUpperCase();
        const accent =
          !this.invoiceProfile.accent_color ||
          this.invoiceProfile.accent_color === 'transparent'
            ? DEFAULT_ACCENT_COLOR
            : this.invoiceProfile.accent_color;
        const payload = {
          ...this.invoiceProfile,
          country: country || null,
          legal_name: this.invoiceProfile.legal_name || null,
          vat_id: this.invoiceProfile.vat_id || null,
          address_line1: this.invoiceProfile.address_line1 || null,
          address_line2: this.invoiceProfile.address_line2 || null,
          city: this.invoiceProfile.city || null,
          postal_code: this.invoiceProfile.postal_code || null,
          region: this.invoiceProfile.region || null,
          invoice_email: this.invoiceProfile.invoice_email || null,
          invoice_phone: this.invoiceProfile.invoice_phone || null,
          accent_color: accent,
        };
        if (this.logoFile) {
          payload.logo = this.logoFile;
        } else if (this.removeLogo) {
          payload.remove_logo = true;
        }
        await this.$store.dispatch('accounts/update', payload);
        this.logoFile = null;
        this.removeLogo = false;
        this.logoUrl = this.currentAccount?.logo_url || '';
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          this.$t('GENERAL_SETTINGS.UPDATE.ERROR');
        useAlert(message);
      }
    },

    onLogoSelected(event) {
      const [file] = event.target.files || [];
      if (!file) return;
      this.logoFile = file;
      this.removeLogo = false;
      this.logoUrl = URL.createObjectURL(file);
    },

    clearLogo() {
      this.logoFile = null;
      this.removeLogo = true;
      this.logoUrl = '';
    },
  },
};
</script>

<template>
  <div>
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccount"
        >
          <WithLabel
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <select v-model="locale" class="!mb-0 text-sm">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyDomainEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domain"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <template #help>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}

              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </template>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyEmailEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <NextInput
              v-model="supportEmail"
              type="text"
              class="w-full"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </WithLabel>
          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <!-- Email & AI Settings Section -->
    <SectionLayout
      :title="$t('GENERAL_SETTINGS.EMAIL_AI_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.EMAIL_AI_SECTION.NOTE')"
    >
      <form
        v-if="!uiFlags.isFetchingItem"
        class="grid gap-4"
        @submit.prevent="updateAccount"
      >
        <WithLabel
          :label="$t('GENERAL_SETTINGS.FORM.SUBJECT_LINE_TEMPLATE.LABEL')"
        >
          <NextInput
            v-model="subjectLineTemplate"
            type="text"
            class="w-full"
            :placeholder="
              $t('GENERAL_SETTINGS.FORM.SUBJECT_LINE_TEMPLATE.PLACEHOLDER')
            "
          />
          <template #help>
            {{ $t('GENERAL_SETTINGS.FORM.SUBJECT_LINE_TEMPLATE.HELP') }}
          </template>
        </WithLabel>
        <WithLabel :label="$t('GENERAL_SETTINGS.FORM.LABEL_PREFIX.LABEL')">
          <NextInput
            v-model="labelPrefix"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.LABEL_PREFIX.PLACEHOLDER')"
          />
          <template #help>
            {{ $t('GENERAL_SETTINGS.FORM.LABEL_PREFIX.HELP') }}
          </template>
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <AudioTranscription v-if="showAudioTranscriptionConfig" />

    <template v-if="showDocumentSigningSettings">
      <!-- Divider -->
      <div class="border-t border-slate-75 my-8" />

      <DocumentSigningSettings />
    </template>

    <template v-if="showAccessLockSettings">
      <!-- Divider -->
      <div class="border-t border-slate-75 my-8" />

      <AccessLockSettings />
    </template>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <!-- Business Information Section -->

    <SectionLayout
      :title="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.NOTE')"
    >
      <form class="grid gap-4" @submit.prevent="updateContentContext">
        <WithLabel
          :label="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.BUSINESS_NAME')"
        >
          <NextInput
            v-model="contentContext.business_name"
            type="text"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.BUSINESS_TYPE')"
        >
          <select v-model="contentContext.business_type" class="!mb-0 text-sm">
            <option value="venue">
              {{ $t('CONTENT.DASHBOARD.TYPES.VENUE') }}
            </option>
            <option value="event_organizer">
              {{ $t('CONTENT.DASHBOARD.TYPES.EVENT_ORGANIZER') }}
            </option>
            <option value="festival">
              {{ $t('CONTENT.DASHBOARD.TYPES.FESTIVAL') }}
            </option>
            <option value="conference_center">
              {{ $t('CONTENT.DASHBOARD.TYPES.CONFERENCE_CENTER') }}
            </option>
            <option value="wedding_venue">
              {{ $t('CONTENT.DASHBOARD.TYPES.WEDDING_VENUE') }}
            </option>
          </select>
        </WithLabel>
        <WithLabel
          :label="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.LOCATION')"
        >
          <TextArea
            v-model="contentContext.location"
            :max-length="500"
            show-character-count
            auto-height
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="
            $t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.TARGET_AUDIENCE')
          "
        >
          <TextArea
            v-model="contentContext.target_audience"
            :max-length="500"
            show-character-count
            auto-height
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="$t('GENERAL_SETTINGS.CONTENT_CONTEXT_SECTION.OFFERINGS')"
        >
          <TextArea
            v-model="contentContext.offerings"
            :max-length="500"
            show-character-count
            auto-height
            class="w-full"
          />
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <!-- Invoice / business details -->
    <SectionLayout
      :title="$t('GENERAL_SETTINGS.INVOICE_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.INVOICE_SECTION.NOTE')"
    >
      <form class="grid gap-4" @submit.prevent="updateInvoiceProfile">
        <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.LOGO')">
          <div class="flex items-center gap-4">
            <div
              class="flex h-16 w-40 items-center justify-center overflow-hidden rounded-md border border-n-weak bg-n-solid-2"
            >
              <img
                v-if="logoUrl"
                :src="logoUrl"
                alt=""
                class="max-h-14 max-w-36 object-contain"
              />
              <span v-else class="text-xs text-n-slate-10">
                {{ $t('GENERAL_SETTINGS.INVOICE_SECTION.LOGO_EMPTY') }}
              </span>
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="inline-flex cursor-pointer items-center justify-center rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white"
              >
                {{ $t('GENERAL_SETTINGS.INVOICE_SECTION.LOGO_UPLOAD') }}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  class="hidden"
                  @change="onLogoSelected"
                />
              </label>
              <button
                v-if="logoUrl"
                type="button"
                class="text-left text-sm text-n-slate-11 hover:text-n-slate-12"
                @click="clearLogo"
              >
                {{ $t('GENERAL_SETTINGS.INVOICE_SECTION.LOGO_REMOVE') }}
              </button>
            </div>
          </div>
        </WithLabel>

        <ColorInput
          v-model="invoiceProfile.accent_color"
          :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.ACCENT_COLOR')"
          :contrast-with="'#FFFFFF'"
        />
        <p class="m-0 -mt-2 text-sm text-n-slate-11">
          {{ $t('GENERAL_SETTINGS.INVOICE_SECTION.ACCENT_COLOR_HELP') }}
        </p>

        <div class="grid gap-4 sm:grid-cols-2">
          <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.LEGAL_NAME')">
            <NextInput
              v-model="invoiceProfile.legal_name"
              type="text"
              class="w-full"
            />
          </WithLabel>
          <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.VAT_ID')">
            <NextInput
              v-model="invoiceProfile.vat_id"
              type="text"
              class="w-full"
            />
          </WithLabel>
        </div>
        <WithLabel
          :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.ADDRESS_LINE1')"
        >
          <NextInput
            v-model="invoiceProfile.address_line1"
            type="text"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.ADDRESS_LINE2')"
        >
          <NextInput
            v-model="invoiceProfile.address_line2"
            type="text"
            class="w-full"
          />
        </WithLabel>
        <div class="grid gap-4 sm:grid-cols-3">
          <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.CITY')">
            <NextInput
              v-model="invoiceProfile.city"
              type="text"
              class="w-full"
            />
          </WithLabel>
          <WithLabel
            :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.POSTAL_CODE')"
          >
            <NextInput
              v-model="invoiceProfile.postal_code"
              type="text"
              class="w-full"
            />
          </WithLabel>
          <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.REGION')">
            <NextInput
              v-model="invoiceProfile.region"
              type="text"
              class="w-full"
            />
          </WithLabel>
        </div>
        <div class="grid gap-4 sm:grid-cols-3">
          <WithLabel :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.COUNTRY')">
            <ComboBox
              v-model="invoiceProfile.country"
              :options="countryOptions"
              :placeholder="
                $t('GENERAL_SETTINGS.INVOICE_SECTION.COUNTRY_PLACEHOLDER')
              "
              class="[&>div>button]:!bg-white [&>div>button]:dark:!bg-n-solid-3"
            />
          </WithLabel>
          <WithLabel
            :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.INVOICE_EMAIL')"
          >
            <NextInput
              v-model="invoiceProfile.invoice_email"
              type="email"
              class="w-full"
            />
          </WithLabel>
          <WithLabel
            :label="$t('GENERAL_SETTINGS.INVOICE_SECTION.INVOICE_PHONE')"
          >
            <NextInput
              v-model="invoiceProfile.invoice_phone"
              type="text"
              class="w-full"
            />
          </WithLabel>
        </div>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <SectionLayout
      :title="$t('GENERAL_SETTINGS.TIMEZONE_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.TIMEZONE_SECTION.NOTE')"
    >
      <form class="grid gap-4" @submit.prevent="updateTimezone">
        <WithLabel :label="$t('GENERAL_SETTINGS.TIMEZONE_SECTION.LABEL')">
          <ComboBox
            v-model="timezone"
            :options="timezoneOptions"
            :placeholder="$t('GENERAL_SETTINGS.TIMEZONE_SECTION.PLACEHOLDER')"
            class="[&>div>button]:!bg-white [&>div>button]:dark:!bg-n-solid-3"
          />
          <template #help>
            {{ $t('GENERAL_SETTINGS.TIMEZONE_SECTION.HELP') }}
          </template>
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <!-- Pricing Defaults Section -->
    <SectionLayout
      :title="$t('GENERAL_SETTINGS.PRICING_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.PRICING_SECTION.NOTE')"
    >
      <form class="grid gap-4" @submit.prevent="updatePricingDefaults">
        <WithLabel :label="$t('GENERAL_SETTINGS.PRICING_SECTION.CURRENCY')">
          <select v-model="defaultCurrency" class="!mb-0 text-sm">
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
            <option value="CHF">CHF</option>
            <option value="SEK">SEK</option>
            <option value="NOK">NOK</option>
            <option value="DKK">DKK</option>
            <option value="PLN">PLN</option>
            <option value="CZK">CZK</option>
          </select>
        </WithLabel>
        <WithLabel :label="$t('GENERAL_SETTINGS.PRICING_SECTION.TAX_RATE')">
          <NextInput
            v-model="defaultTaxRate"
            type="number"
            class="w-full"
            :placeholder="
              $t('GENERAL_SETTINGS.PRICING_SECTION.TAX_RATE_PLACEHOLDER')
            "
          />
          <template #help>
            {{ $t('GENERAL_SETTINGS.PRICING_SECTION.TAX_RATE_HELP') }}
          </template>
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
      <!-- Divider -->
      <div class="border-t border-slate-75 my-8" />
      <AccountDelete />
    </div>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <SectionLayout
      :title="$t('GENERAL_SETTINGS.FORM.WEBHOOK_SIGNATURE_SECRET.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.WEBHOOK_SIGNATURE_SECRET.NOTE')"
    >
      <AccessToken
        :value="getWebhookSignatureSecret"
        @on-copy="onCopyToken"
        @on-reset="onResetWebhookSecret"
      />
    </SectionLayout>

    <!-- Divider -->
    <div class="border-t border-slate-75 my-8" />

    <AccountId />
    <BuildInfo />
  </div>
</template>
